import './Metrics.scss';
import { useTranslator } from '@Localization';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { IValidJobType, JOB_STATUS } from '../../../jobs.model';
import { IJobThreshold } from '../../../jobThreshold.store';
import { getJobTranslationPrefix, useDebouncedEffect } from '../../../utils';
import { RecalculateMetricsSlider } from '../../SliderWithLabel';

interface IProps {
  jobType: IValidJobType;
  children: ReactNode;
  recalculateMetrics?: (min: number, max: number) => void;
  threshold?: IJobThreshold | null;
  saveThresholds?: (min: number, max: number) => void;
  status: JOB_STATUS;
}

export const MetricsBase = ({ children, jobType, recalculateMetrics, threshold, saveThresholds, status }: IProps): ReactElement => {
  const { t } = useTranslator();
  const [init, setInit] = useState(true);
  const [values, setValues] = useState<number[]>();
  const [inputsValues, setInputsValues] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [isSaving, setIsSaving] = useState(false);
  const translationPrefix = getJobTranslationPrefix(jobType);

  useEffect(() => {
    if (threshold && init) {
      const min = threshold.minThreshold;
      const max = threshold.maxThreshold;

      if (min === null || max === null) {
        return;
      }

      setInputsValues(() => ({ min: min.toFixed(1), max: max.toFixed(1) }));
      setInit(false);
    }
  }, [threshold, init]);

  useDebouncedEffect(
    () => {
      const min = parseFloat(inputsValues.min);
      const max = parseFloat(inputsValues.max);

      if (isNaN(min) || isNaN(max) || !threshold) {
        return;
      }

      if (min < threshold.minScore) {
        setInputsValues((state) => ({ ...state, min: threshold.minScore.toFixed(1) }));
        return;
      }

      if (max > threshold.maxScore) {
        setInputsValues((state) => ({ ...state, max: threshold.maxScore.toFixed(1) }));
        return;
      }

      if (min > max) {
        setInputsValues((state) => ({ ...state, min: max.toFixed(1) }));
        return;
      }

      setValues([min, max]);
    },
    [inputsValues],
    500
  );

  useEffect(() => {
    if (!values) {
      return;
    }

    if (recalculateMetrics) {
      recalculateMetrics(values[0], values[1]);
    }
  }, [values]);

  const handleChange = (values: readonly number[]): void => {
    setInputsValues(() => ({ min: values[0].toFixed(1), max: values[1].toFixed(1) }));
  };

  const handleChangeInput =
    (title: string) =>
    (value: string): void => {
      setInputsValues((state) => ({ ...state, [title]: value }));
    };

  const saveThresholdsHandler = async (): Promise<void> => {
    if (!(!saveThresholds || !values || isSaving)) {
      setIsSaving(true);
      saveThresholds(values[0], values[1]);
      setIsSaving(false);
    }
    return Promise.resolve();
  };

  return (
    <div className='result-metrics'>
      <div className='results-header-container'>
        <div>
          <h4>{t('metrics.title')}</h4>
          {![JOB_STATUS.FINISHED, JOB_STATUS.IMPORTED].includes(status) && <p>{t(`${translationPrefix}.available_after`)}</p>}
        </div>
        {!!threshold && values && (
          <RecalculateMetricsSlider
            label='Threshold'
            value={values}
            min={threshold.minScore}
            max={threshold.maxScore}
            step={threshold.thresholdStep}
            onChange={handleChange}
            onInputChanged={handleChangeInput}
            inputsValues={inputsValues}
            saveThresholdsHandler={saveThresholdsHandler}
            isSaving={isSaving || (status && status === JOB_STATUS.SAVING)}
          />
        )}
      </div>
      {children}
    </div>
  );
};
